import { computed, inject, Injectable, signal } from '@angular/core';
import { PackageDto, PackageOptionDto } from '../../dto';
import { Package, PackagesService } from '@webmodule-order/api';
import { catchError, tap } from 'rxjs';
import { AbstractStateServiceComponent, baseState, DefaultState } from '../../utils/abstract-state-service.component';

interface State extends DefaultState {
  packages: PackageDto[];
  selected: PackageDto | null;
  options: PackageOptionDto[];
}

const initialState: State = {
  ...baseState,
  packages: [],
  selected: null,
  options: [],
};

@Injectable({
  providedIn: 'root',
})
export class ClientPackageService extends AbstractStateServiceComponent {
  private readonly apiService = inject(PackagesService);
  override state = signal(initialState);
  readonly packages = computed(() => this.state().packages);
  readonly selected = computed(() => this.state().selected);
  readonly tvExtraOptions = computed(() => this.state().options.filter((option) => option.tag?.includes('tv_extra')));
  readonly phoneOptions = computed(() => this.state().options.filter((option) => option.tag?.includes('phone')));

  fetchPackages(locationId: number) {
    this.setLoading(true);
    this.state.set({ ...this.state(), packages: [] });

    return this.apiService.getPackagesLocationId(locationId).pipe(
      tap((res) => this.setState(res)),
      catchError((error) => {
        this.setError(error.message);
        return error;
      }),
    );
  }

  select(item: PackageDto | null) {
    const options =
      item?.packageOptionDefinitions.map((item) => {
        return {
          ...item.packageOption,
          isPartOfPackage: item.isPartOfPackage,
        } as PackageOptionDto;
      }) || [];

    this.state.set({ ...this.state(), selected: item, options: options });
  }

  override reset() {
    super.reset();

    this.state.set({ ...this.state(), packages: [] });
  }

  override loadSessionState(key: string) {
    const sessionState = sessionStorage.getItem(key);
    if (!key || !sessionState) {
      return Promise.reject(`no session storage for ${key}`);
    }
    const jsonSessionState = JSON.parse(sessionState) as State;

    if (!jsonSessionState.packages) {
      return Promise.reject(`has session but not packages`);
    }

    return Promise.resolve(jsonSessionState.packages);
  }

  /**
   * Convert Package to PackageDto
   */
  setState(packages: Package[]) {
    const packagesDto = packages.map(
      (item) =>
        ({
          ...item,
          upload: Number(item.speedUp),
          download: Number(item.speedDown),
          priceAsNumber: parseFloat(item.price ?? '0'),
        } as PackageDto),
    );

    this.state.set({ ...this.state(), packages: packagesDto });
    this.setLoading(false);
    this.setLoaded(true);

    this.setSessionStorage('packageState');
  }
}
